/*
 * article-toc.js — blog article template enhancements (no dependencies, deferred).
 *
 * Heading ids and both TOC link lists are rendered SERVER-SIDE by
 * sections/main-article.liquid (so anchors work without JS and nothing
 * shifts at load). This file only:
 *   1. runs the rail scrollspy (IntersectionObserver),
 *   2. wires the copy-link share buttons (canonical URL from data-copy-url),
 *   3. wraps FAQ paragraphs (p starting with <strong> after an "FAQ" h2)
 *      in .article-faq-item panels,
 *   4. collapses the mobile <details> TOC after a link is tapped.
 * Re-inits on shopify:section:load for the theme editor.
 */
(function () {
  'use strict';

  function init() {
    var content = document.querySelector('.article-template__content');
    if (content) wrapFaq(content);
    initToc();
    initCopy();
  }

  /* 3. FAQ panels — wrap each p whose first node is a <strong> question,
     between the FAQ h2 and the next h2. The FAQ h2 is the server-stamped
     h2[data-faq-section] when present (the product template's Liquid pass
     stamps it and feeds the FAQPage JSON-LD from the SAME section, so the
     visual wrap can never drift from the schema); blog articles carry no
     marker and keep the original last-h2-matching-/\bfaq\b/i scan. */
  function wrapFaq(content) {
    var faqH2 = content.querySelector('h2[data-faq-section]');
    if (!faqH2) {
      var h2s = content.querySelectorAll('h2');
      for (var i = 0; i < h2s.length; i++) {
        if (/\bfaq\b/i.test(h2s[i].textContent)) faqH2 = h2s[i];
      }
    }
    if (!faqH2) return;
    var node = faqH2.nextElementSibling;
    while (node && node.tagName !== 'H2') {
      var next = node.nextElementSibling;
      if (node.tagName === 'P' && !node.closest('.article-faq-item') && startsWithStrong(node)) {
        var wrap = document.createElement('div');
        wrap.className = 'article-faq-item';
        node.parentNode.insertBefore(wrap, node);
        wrap.appendChild(node);
      }
      node = next;
    }
  }

  function startsWithStrong(p) {
    var child = p.firstChild;
    while (child && child.nodeType === 3 && !child.textContent.trim()) {
      child = child.nextSibling;
    }
    return !!(child && child.nodeType === 1 && child.tagName === 'STRONG');
  }

  /* 1 + 4. Scrollspy on the rail links; click sets active immediately
     (heals the short-last-section gap) and closes the mobile details. */
  function initToc() {
    var rail = document.querySelector('.article-toc');
    if (!rail || rail.dataset.spyBound) return;
    rail.dataset.spyBound = '1';
    var railLinks = rail.querySelectorAll('.article-toc__link');
    if (!railLinks.length) return;

    var byHref = {};
    var headingMap = [];
    railLinks.forEach(function (link) {
      var href = link.getAttribute('href') || '';
      byHref[href] = link;
      var id = decodeURIComponent(href.slice(1));
      var heading = document.getElementById(id);
      if (heading) headingMap.push([heading, link]);
    });

    var activeLink = null;
    function setActive(link) {
      if (!link || link === activeLink) return;
      if (activeLink) {
        activeLink.classList.remove('is-active');
        activeLink.removeAttribute('aria-current');
      }
      activeLink = link;
      activeLink.classList.add('is-active');
      activeLink.setAttribute('aria-current', 'true');
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            headingMap.forEach(function (pair) {
              if (pair[0] === entry.target) setActive(pair[1]);
            });
          });
        },
        { rootMargin: '0px 0px -70% 0px' }
      );
      headingMap.forEach(function (pair) {
        observer.observe(pair[0]);
      });
    }

    document.querySelectorAll('.article-toc__link').forEach(function (link) {
      if (link.dataset.tocBound) return;
      link.dataset.tocBound = '1';
      link.addEventListener('click', function () {
        setActive(byHref[link.getAttribute('href')] || null);
        var details = link.closest('details');
        if (details) details.open = false;
      });
    });

    // Initial state: honor a deep-linked fragment, else the first heading.
    setActive(byHref[window.location.hash] || railLinks[0]);
  }

  /* 2. Copy-link buttons — copy the server-rendered canonical URL. */
  function initCopy() {
    document.querySelectorAll('.article-share__copy').forEach(function (button) {
      if (button.dataset.copyBound) return;
      button.dataset.copyBound = '1';
      button.addEventListener('click', function () {
        var url = button.getAttribute('data-copy-url') || window.location.href;
        copyText(url).then(function (ok) {
          if (!ok) return;
          button.classList.add('is-copied');
          var status = button.querySelector('[data-copy-status]');
          if (status) status.textContent = button.getAttribute('data-copy-success') || 'Link copied';
          window.setTimeout(function () {
            button.classList.remove('is-copied');
            if (status) status.textContent = '';
          }, 2000);
        });
      });
    });
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(
        function () { return true; },
        function () { return copyTextFallback(text); }
      );
    }
    return Promise.resolve(copyTextFallback(text));
  }

  function copyTextFallback(text) {
    var ok = false;
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try {
      ok = document.execCommand('copy');
    } catch (error) {
      ok = false;
    }
    area.remove();
    return ok;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
